import { Link } from "react-router-dom";
import { Network, ArrowRight, Check } from "lucide-react";
import { ARCH_INTEGRATIONS } from "@/lib/partnership";
import { LIVE_SYSTEMS } from "@/lib/liveOps";

export default function TechnicalView() {
  return (
    <div className="space-y-6" data-testid="technical-view">
      <div>
        <div className="text-[10px] font-mono tracking-widest text-emerald-500 uppercase mb-1">Technical View · Simulated demo data</div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-100 flex items-center gap-3">
          <Network className="w-7 h-7 text-emerald-400" strokeWidth={1.5} /> Connected Systems &amp; Event Flow
        </h1>
        <p className="text-sm text-zinc-400 mt-1 max-w-3xl">
          O.N.E.Tech connects to the broker's existing stack — CRM, trading platform, payments, KYC and communication
          channels remain the systems of record. Nothing is replaced.
        </p>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5" data-testid="tech-system-health">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <span className="text-[10px] font-mono tracking-widest text-zinc-500 uppercase">Connected systems</span>
          <span className="text-[10px] font-mono text-zinc-500">Last event received: 3 sec ago · Queue latency: 84 ms · Failed connectors: 0</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {LIVE_SYSTEMS.map((s) => (
            <div key={s.key} className="border border-zinc-800 bg-zinc-950/50 rounded-sm p-3.5" data-testid={`tech-system-${s.key}`}>
              <div className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-[10px] font-mono text-emerald-500">CONNECTED</span>
              </div>
              <div className="text-xs font-semibold text-zinc-100 mt-1.5">{s.name}</div>
              <div className="text-[9px] font-mono text-zinc-600 mt-0.5">simulated feed</div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5" data-testid="tech-integrations">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <span className="text-sm font-semibold text-zinc-200">Integration summary — read / decide / write-back</span>
          <Link to="/partnership/architecture" data-testid="tech-open-architecture"
            className="flex items-center gap-1.5 text-xs font-semibold text-emerald-400 hover:text-emerald-300 transition-colors duration-150">
            Full integration architecture <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.5} />
          </Link>
        </div>
        <div className="space-y-2.5">
          {ARCH_INTEGRATIONS.map((i) => (
            <div key={i.key} className="border border-zinc-800 bg-zinc-950/50 rounded-sm p-4" data-testid={`tech-integration-${i.key}`}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="text-xs font-semibold text-zinc-100">{i.name}</span>
                <span className="text-[9px] font-mono text-amber-500/90 border border-amber-500/20 bg-amber-500/5 rounded-sm px-2 py-0.5">SIMULATED FOR DEMONSTRATION</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[11px]">
                <div><div className="text-[9px] font-mono text-emerald-500 uppercase tracking-wider mb-0.5">Reads</div><span className="text-zinc-400">{i.read}</span></div>
                <div><div className="text-[9px] font-mono text-emerald-500 uppercase tracking-wider mb-0.5">Produces</div><span className="text-zinc-400">{i.output}</span></div>
                <div><div className="text-[9px] font-mono text-emerald-500 uppercase tracking-wider mb-0.5">Writes back</div><span className="text-zinc-400">{i.writeback}</span></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-5 flex items-start gap-3" data-testid="tech-audit-note">
        <Check className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" strokeWidth={1.5} />
        <p className="text-xs text-zinc-400 leading-relaxed">
          Every event, decision, approval, execution and write-back is recorded in the immutable audit log
          (Governance Centre). Event normalisation, policy evaluation and orchestration run inside the O.N.E.Tech
          layer; the broker's systems remain authoritative for client records, funds and KYC decisions.
        </p>
      </div>
    </div>
  );
}
